import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  Category as CategoryIcon,
  AccountTree as SubCategoryIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";

const drawerWidth = 280;

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem("login");
    navigate("/");
  };

  const menuItems = [
    { icon: <InventoryIcon />, label: "Products", path: "/admin/Products" },
    { icon: <OrdersIcon />, label: "Orders", path: "/admin/Orders" },
    { icon: <CategoryIcon />, label: "Categories", path: "/admin/Category" },
    { icon: <SubCategoryIcon />, label: "Sub Categories", path: "/admin/SubCategory" },
  ];

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#1565c0" }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          bgcolor: "#1976d2",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "#fff",
            color: "#1565c0",
            fontWeight: "bold",
            fontSize: "1.5rem",
          }}
        >
          N
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
            Nut Admin
          </Typography>
          <Typography variant="caption" sx={{ color: "#90caf9" }}>
            Management Panel
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#1976d2" }} />

      {/* Admin Profile Card */}
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: "#1976d2",
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "#fff",
              color: "#1565c0",
              mx: "auto",
              mb: 1,
              fontSize: "2rem",
              fontWeight: "bold",
            }}
          >
            A
          </Avatar>
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600 }}>
            Administrator
          </Typography>
          <Typography variant="caption" sx={{ color: "#90caf9" }}>
            Super Admin
          </Typography>
        </Box>
      </Box>

      {/* Menu Items */}
      <List sx={{ px: 2, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  bgcolor: isActive ? "#1976d2" : "transparent",
                  "&:hover": {
                    bgcolor: isActive ? "#1976d2" : "#1e88e5",
                  },
                  py: 1.5,
                }}
              >
                <ListItemIcon sx={{ color: isActive ? "#fff" : "#90caf9", minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#fff" : "#e3f2fd",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "#1976d2" }} />

      {/* Logout */}
      <List sx={{ p: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              "&:hover": { bgcolor: "#d32f2f" },
              bgcolor: "#c62828",
              py: 1.5,
            }}
          >
            <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                color: "#fff",
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: "#fff",
          color: "#1a237e",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
}
